import { useId } from 'react';

import type { SelectedPhoto } from './usePhotoSelection';

/**
 * Выбор фотографий с превью.
 *
 * Файлы остаются в памяти вкладки до нажатия «Создать портрет» — ровно
 * поэтому здесь нет ни прогресса загрузки, ни отмены: загружать пока нечего.
 */
interface PhotoPickerProps {
  photos: SelectedPhoto[];
  issues: string[];
  max: number;
  disabled?: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}

export function PhotoPicker({ photos, issues, max, disabled, onAdd, onRemove }: PhotoPickerProps) {
  const inputId = useId();

  return (
    <section className="flex flex-col gap-2.5" aria-labelledby={`${inputId}-title`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={`${inputId}-title`} className="text-sm font-medium text-text-2">
          Ваши фото
        </h2>
        <span className="text-xs text-text-3 tabular-nums">
          {photos.length} из {max}
        </span>
      </div>

      <ul className="grid grid-cols-3 gap-2">
        {photos.map((photo, index) => (
          <li
            key={photo.previewUrl ?? photo.file.name}
            className="relative aspect-3/4 overflow-hidden rounded-xl border border-border bg-surface"
          >
            {photo.previewUrl ? (
              <img
                src={photo.previewUrl}
                alt={`Фотография ${index + 1}`}
                className="h-full w-full object-cover"
              />
            ) : (
              // HEIC браузер не отрисует, но файл пригоден: сервер его примет.
              <p className="flex h-full items-center justify-center p-2 text-center text-xs text-text-3">
                Формат HEIC — превью недоступно
              </p>
            )}

            <span className="absolute inset-x-0 bottom-0 truncate bg-scrim/80 px-1.5 py-1 text-xs text-on-scrim">
              {photo.file.name}
            </span>

            <button
              type="button"
              aria-label={`Удалить фотографию ${index + 1}`}
              onClick={() => onRemove(index)}
              className="absolute top-1 right-1 h-6 w-6 rounded-full bg-scrim/75 text-on-scrim"
            >
              ×
            </button>
          </li>
        ))}

        {photos.length < max && (
          <li>
            <label
              htmlFor={inputId}
              className={`flex aspect-3/4 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border-strong text-xs text-text-2 hover:border-accent hover:text-text ${
                disabled ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              <span aria-hidden className="text-xl leading-none">
                +
              </span>
              Добавить
            </label>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              multiple
              disabled={disabled}
              aria-label="Добавить фотографию"
              className="sr-only"
              onChange={(event) => {
                onAdd([...(event.target.files ?? [])]);
                // Тот же файл должен выбираться повторно после удаления.
                event.target.value = '';
              }}
            />
          </li>
        )}
      </ul>

      {issues.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {issues.map((issue) => (
            <li key={issue} className="text-xs text-danger">
              {issue}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-text-3">
        Лицо крупно, хороший свет. Можно несколько снимков одного человека.
      </p>
    </section>
  );
}
